export const colors = {
  primary: 'var(--voyager-color-primary, #440f95)',
} as const;

export const fonts = {
  heading: "var(--voyager-heading-font, 'PP Eiko', 'Playfair Display'), serif",
  body: "var(--voyager-body-font, 'Suisse Intl', 'Inter'), sans-serif",
  baseSize: '16px',
  size: {
    // Prefer `textStyle.*` for new code — this ramp is retained for existing call sites.
    xxs: '0.5625rem',
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    base: '1rem',
    lg: '1.25rem',
    xl: '1.5rem',
    '2xl': '1.75rem',
    '3xl': '2.5rem',
    '4xl': '3rem',
  },
  weight: {
    normal: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
  },
} as const;

export const headingFont = fonts.heading;
export const bodyFont = fonts.body;

export const breakpoints = {
  mobile: '576px',
  tablet: '768px',
  desktop: '992px',
  wide: '1200px',
} as const;

export const layout = {
  maxWidth: '1480px',
  narrowMaxWidth: '860px',
  horizontalPadding: '20px',
  headerHeightMobile: '81px',
  headerHeightDesktop: '111px',
  mobileNavHeight: 'calc(80px + env(safe-area-inset-bottom, 0px))',
  sideNavWidth: '100px',
  heroImageMobileHeight: '260px',
  desktopBreakpoint: '769px',
  inputPadding: '16px 20px',
  zIndexDropdown: 1000,
  zIndexModal: 10000,
  modalWidth: '420px',
  modalWidthLg: '600px',
} as const;

export const primitives = {
  color: {
    blackAlpha: {
      50: '#0d0d0d0d',
      100: '#0d0d0d1a',
      200: '#0d0d0d33',
      400: '#0d0d0d66',
      600: '#0d0d0d99',
      1000: '#0d0d0d',
    },
    whiteAlpha: {
      50: '#ffffff0d',
      100: '#ffffff1a',
      400: '#ffffff66',
      1000: '#ffffff',
    },
    black: {
      'color-black-alpha-100': '#0d0d0d06',
      'color-black-alpha-200': '#0d0d0d0d',
      'color-black-alpha-300': '#0d0d0d13',
      'color-black-alpha-400': '#0d0d0d1a',
      'color-black-alpha-500': '#0d0d0d33',
      'color-black-alpha-600': '#0d0d0d66',
      'color-black-alpha-700': '#0d0d0d99',
      'color-black-alpha-800': '#0d0d0dcc',
      'color-black-alpha-900': '#0d0d0de6',
      'color-black-alpha-1000': '#0d0d0d',
    },
    white: {
      'color-white-alpha-100': '#ffffff06',
      'color-white-alpha-200': '#ffffff0d',
      'color-white-alpha-300': '#ffffff13',
      'color-white-alpha-400': '#ffffff1a',
      'color-white-alpha-500': '#ffffff33',
      'color-white-alpha-600': '#ffffff66',
      'color-white-alpha-700': '#ffffff99',
      'color-white-alpha-800': '#ffffffcc',
      'color-white-alpha-900': '#ffffffe6',
      'color-white-alpha-1000': '#ffffff',
    },
    blue: {
      'color-blue-100': '#e6f0fe',
      'color-blue-600': '#184cb4',
      'color-blue-700': '#103a93',
    },
    green: {
      'color-green-100': '#e7fded',
      'color-green-600': '#178c3c',
      'color-green-700': '#0c6e2a',
    },
    yellow: {
      'color-yellow-100': '#fef5e6',
      'color-yellow-600': '#e08700',
      'color-yellow-700': '#c27400',
    },
    red: {
      'color-red-100': '#fee6e9',
      'color-red-600': '#be0e2c',
      'color-red-700': '#980b23',
    },
  },
  typography: {
    fontFamily: {
      brandPrimary: "var(--voyager-heading-font, 'PP Eiko', 'Playfair Display'), serif",
      brandSecondary: "var(--voyager-body-font, 'Suisse Intl', 'Inter'), sans-serif",
    },
    fontWeight: {
      regular: 400,
      medium: 500,
    },
    tracking: {
      tightest: '-1px',
      tight: '-0.5px',
      normal: '0',
      wide: '0.5px',
    },
    lineHeight: {
      tight: 1.2,
      snug: 1.25,
      normal: 1.4,
      relaxed: 1.5,
      loose: 1.6,
    },
    weight: {
      'weight-400': 400,
      'weight-500': 500,
    },
    'line-height': {
      'line-height-100': 1.2,
      'line-height-200': 1.4,
    },
    track: {
      'track-neg-100': '-1px',
      'track-neg-050': '-0.5px',
      'track-0': '0',
      'track-050': '0.5px',
    },
    'scale-text': {
      'scale-text-100': '0.625rem',
      'scale-text-200': '0.75rem',
      'scale-text-300': '0.875rem',
      'scale-text-400': '1rem',
      'scale-text-500': '1.25rem',
      'scale-text-600': '1.5rem',
      'scale-text-700': '1.75rem',
      'scale-text-800': '2rem',
      'scale-text-900': '2.25rem',
      'scale-text-1000': '2.5rem',
      'scale-text-1100': '3rem',
    },
  },
  size: {
    'size-0': '0px',
    'size-025': '1px',
    'size-050': '2px',
    'size-100': '4px',
    'size-200': '8px',
    'size-300': '12px',
    'size-400': '16px',
    'size-500': '20px',
    'size-600': '24px',
    'size-700': '28px',
    'size-800': '32px',
    'size-900': '36px',
    'size-1000': '40px',
    'size-1100': '44px',
    'size-1200': '48px',
    'size-1400': '56px',
    'size-1600': '64px',
    'size-2000': '80px',
    'size-2400': '96px',
    'size-3000': '120px',
    'size-4000': '160px',
    'size-6000': '240px',
    'size-full': '9999px',
    'size-neg-025': '-1px',
    'size-neg-050': '-2px',
    'size-neg-100': '-4px',
    'size-neg-200': '-8px',
    'size-neg-400': '-16px',
    'size-neg-800': '-32px',
    'size-neg-1200': '-48px',
  },
} as const;

export const space = {
  'space-0': primitives.size['size-0'],
  'space-050': primitives.size['size-050'],
  'space-100': primitives.size['size-100'],
  'space-200': primitives.size['size-200'],
  'space-300': primitives.size['size-300'],
  'space-400': primitives.size['size-400'],
  'space-500': primitives.size['size-500'],
  'space-600': primitives.size['size-600'],
  'space-700': primitives.size['size-700'],
  'space-800': primitives.size['size-800'],
  'space-900': primitives.size['size-900'],
  'space-1000': primitives.size['size-1000'],
  'space-1100': primitives.size['size-1100'],
  'space-1200': primitives.size['size-1200'],
  'space-1400': primitives.size['size-1400'],
  'space-1600': primitives.size['size-1600'],
  'space-2000': primitives.size['size-2000'],
  'space-2400': primitives.size['size-2400'],
  'space-3000': primitives.size['size-3000'],
  'space-4000': primitives.size['size-4000'],
  'space-6000': primitives.size['size-6000'],
  'space-neg-400': primitives.size['size-neg-400'],
} as const;

export const size = {
  radius: {
    'radius-xs': primitives.size['size-100'],
    'radius-sm': primitives.size['size-200'],
    'radius-md': primitives.size['size-300'],
    'radius-lg': primitives.size['size-400'],
    'radius-xl': primitives.size['size-600'],
    'radius-xxl': primitives.size['size-800'],
    'radius-full': primitives.size['size-full'],
  },
  blur: {
    'blur-sm': primitives.size['size-400'],
    'blur-md': primitives.size['size-800'],
    'blur-lg': primitives.size['size-1000'],
  },
  stroke: {
    'stroke-default': primitives.size['size-025'],
    'stroke-thick': primitives.size['size-050'],
  },
  icon: {
    'icon-sm': primitives.size['size-500'],
    'icon-md': primitives.size['size-600'],
    'icon-lg': primitives.size['size-800'],
    'icon-xl': primitives.size['size-1000'],
  },
  depth: {
    'depth-none': primitives.size['size-0'],
    'depth-xs': primitives.size['size-100'],
    'depth-sm': primitives.size['size-200'],
    'depth-md': primitives.size['size-400'],
    'depth-lg': primitives.size['size-800'],
    'depth-xl': primitives.size['size-1200'],
    'depth-neg-sm': primitives.size['size-neg-200'],
    'depth-neg-md': primitives.size['size-neg-400'],
    'depth-neg-lg': primitives.size['size-neg-800'],
  },
} as const;

export const shadow = {
  xs: `0 ${size.depth['depth-xs']} calc(${size.blur['blur-sm']} / 2) ${size.depth['depth-none']} ${primitives.color.black['color-black-alpha-400']}`,
  sm: `0 ${size.depth['depth-xs']} ${size.blur['blur-sm']} ${size.depth['depth-none']} ${primitives.color.black['color-black-alpha-400']}`,
  lg: `0 ${size.depth['depth-xs']} ${size.blur['blur-lg']} ${size.depth['depth-none']} ${primitives.color.black['color-black-alpha-400']}`,
} as const;

export const theme = {
  color: {
    'theme-color-brand-primary':
      'var(--voyager-theme-color-brand-primary, var(--voyager-color-primary, #440f95))',
    'theme-color-brand-bg': 'var(--voyager-theme-color-brand-bg, #ffffff)',
  },
  fonts: {
    'theme-font-brand-primary': fonts.body,
    'theme-font-brand-secondary': fonts.heading,
  },
} as const;

export const semantic = {
  text: {
    primary: `var(--voyager-text-primary, ${primitives.color.black['color-black-alpha-1000']})`,
    secondary: `var(--voyager-text-secondary, ${primitives.color.black['color-black-alpha-700']})`,
    tertiary: `var(--voyager-text-tertiary, ${primitives.color.black['color-black-alpha-600']})`,
    inverse: `var(--voyager-text-inverse, ${primitives.color.white['color-white-alpha-1000']})`,
    disabled: `var(--voyager-text-disabled, ${primitives.color.black['color-black-alpha-400']})`,
    brand: `var(--voyager-text-brand, ${theme.color['theme-color-brand-primary']})`,
    danger: `var(--voyager-text-danger, ${primitives.color.red['color-red-600']})`,
    positive: `var(--voyager-text-positive, ${primitives.color.green['color-green-600']})`,
    info: `var(--voyager-text-info, ${primitives.color.blue['color-blue-600']})`,
    warning: `var(--voyager-text-warning, ${primitives.color.yellow['color-yellow-600']})`,
  },
  bg: {
    primary: `var(--voyager-bg-primary, ${primitives.color.black['color-black-alpha-1000']})`,
    secondary: `var(--voyager-bg-secondary, ${primitives.color.white['color-white-alpha-1000']})`,
    canvas: `var(--voyager-bg-canvas, ${theme.color['theme-color-brand-bg']})`,
    brand: `var(--voyager-bg-brand, ${theme.color['theme-color-brand-primary']})`,
    surfaceSubtle: `var(--voyager-bg-surface-subtle, ${primitives.color.black['color-black-alpha-200']})`,
    surfaceDefault: `var(--voyager-bg-surface-default, ${primitives.color.black['color-black-alpha-400']})`,
    surfaceStrong: `var(--voyager-bg-surface-strong, ${primitives.color.black['color-black-alpha-600']})`,
    surfaceInverseSubtle: `var(--voyager-bg-surface-inverse-subtle, ${primitives.color.white['color-white-alpha-200']})`,
    surfaceInverseDefault: `var(--voyager-bg-surface-inverse-default, ${primitives.color.white['color-white-alpha-400']})`,
    surfaceInverseStrong: `var(--voyager-bg-surface-inverse-strong, ${primitives.color.white['color-white-alpha-600']})`,
    overlayBlur: `var(--voyager-bg-overlay-blur, ${primitives.color.white['color-white-alpha-200']})`,
    overlayBlurStrong: `var(--voyager-bg-overlay-blur-strong, ${primitives.color.white['color-white-alpha-400']})`,
    overlayImage: `var(--voyager-bg-overlay-image, ${primitives.color.black['color-black-alpha-400']})`,
    overlayModal: `var(--voyager-bg-overlay-modal, ${primitives.color.black['color-black-alpha-500']})`,
    disabled: `var(--voyager-bg-disabled, ${primitives.color.black['color-black-alpha-200']})`,
    infoSubtle: `var(--voyager-bg-info-subtle, ${primitives.color.blue['color-blue-100']})`,
    infoStrong: `var(--voyager-bg-info-strong, ${primitives.color.blue['color-blue-700']})`,
    positiveSubtle: `var(--voyager-bg-positive-subtle, ${primitives.color.green['color-green-100']})`,
    positiveStrong: `var(--voyager-bg-positive-strong, ${primitives.color.green['color-green-700']})`,
    dangerSubtle: `var(--voyager-bg-danger-subtle, ${primitives.color.red['color-red-100']})`,
    dangerStrong: `var(--voyager-bg-danger-strong, ${primitives.color.red['color-red-700']})`,
    warningSubtle: `var(--voyager-bg-warning-subtle, ${primitives.color.yellow['color-yellow-100']})`,
    warningStrong: `var(--voyager-bg-warning-strong, ${primitives.color.yellow['color-yellow-700']})`,
    urgencyStrong: `var(--voyager-bg-urgency-strong, ${primitives.color.red['color-red-700']})`,
  },
  border: {
    default: `var(--voyager-border-default, ${primitives.color.black['color-black-alpha-400']})`,
    primary: `var(--voyager-border-primary, ${primitives.color.black['color-black-alpha-400']})`,
    secondary: `var(--voyager-border-secondary, ${primitives.color.black['color-black-alpha-500']})`,
    subtle: `var(--voyager-border-subtle, ${primitives.color.black['color-black-alpha-200']})`,
    inverse: `var(--voyager-border-inverse, ${primitives.color.white['color-white-alpha-600']})`,
    selected: `var(--voyager-border-selected, ${primitives.color.black['color-black-alpha-1000']})`,
    disabled: `var(--voyager-border-disabled, ${primitives.color.black['color-black-alpha-400']})`,
    brand: `var(--voyager-border-brand, ${theme.color['theme-color-brand-primary']})`,
    danger: `var(--voyager-border-danger, ${primitives.color.red['color-red-700']})`,
    positive: `var(--voyager-border-positive, ${primitives.color.green['color-green-700']})`,
    warning: `var(--voyager-border-warning, ${primitives.color.yellow['color-yellow-700']})`,
    info: `var(--voyager-border-info, ${primitives.color.blue['color-blue-700']})`,
  },
  icon: {
    primary: `var(--voyager-icon-primary, ${primitives.color.black['color-black-alpha-1000']})`,
    secondary: `var(--voyager-icon-secondary, ${primitives.color.black['color-black-alpha-700']})`,
    tertiary: `var(--voyager-icon-tertiary, ${primitives.color.black['color-black-alpha-600']})`,
    inverse: `var(--voyager-icon-inverse, ${primitives.color.white['color-white-alpha-1000']})`,
    disabled: `var(--voyager-icon-disabled, ${primitives.color.black['color-black-alpha-400']})`,
    danger: `var(--voyager-icon-danger, ${primitives.color.red['color-red-600']})`,
    positive: `var(--voyager-icon-positive, ${primitives.color.green['color-green-600']})`,
    warning: `var(--voyager-icon-warning, ${primitives.color.yellow['color-yellow-600']})`,
    info: `var(--voyager-icon-info, ${primitives.color.blue['color-blue-600']})`,
  },
} as const;

export const component = {
  button: {
    primary: {
      enabled: {
        bg: semantic.bg.brand,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      hover: {
        bg: `color-mix(in srgb, ${semantic.bg.brand} 60%, ${primitives.color.black['color-black-alpha-1000']})`,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      focussed: {
        bg: `color-mix(in srgb, ${semantic.bg.brand} 90%, ${primitives.color.white['color-white-alpha-1000']})`,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      pressed: {
        bg: `color-mix(in srgb, ${semantic.bg.brand} 60%, ${primitives.color.black['color-black-alpha-1000']})`,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      disabled: {
        bg: semantic.bg.surfaceSubtle,
        text: semantic.text.disabled,
        icon: semantic.icon.disabled,
      },
    },
    danger: {
      enabled: {
        bg: semantic.bg.urgencyStrong,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      hover: {
        bg: `color-mix(in srgb, ${semantic.bg.urgencyStrong} 95%, ${primitives.color.black['color-black-alpha-1000']})`,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      focussed: {
        bg: `color-mix(in srgb, ${semantic.bg.urgencyStrong} 95%, ${primitives.color.white['color-white-alpha-1000']})`,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      pressed: {
        bg: `color-mix(in srgb, ${semantic.bg.urgencyStrong} 60%, ${primitives.color.white['color-white-alpha-1000']})`,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      disabled: {
        bg: semantic.bg.surfaceSubtle,
        text: semantic.text.disabled,
        icon: semantic.icon.disabled,
      },
    },
    secondary: {
      enabled: {
        bg: semantic.bg.secondary,
        text: semantic.text.primary,
        icon: semantic.icon.primary,
      },
      hover: {
        bg: semantic.bg.primary,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      focussed: {
        bg: semantic.bg.surfaceDefault,
        text: semantic.text.primary,
        icon: semantic.icon.primary,
      },
      pressed: {
        bg: semantic.bg.primary,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      disabled: {
        bg: semantic.bg.disabled,
        text: semantic.text.disabled,
        icon: semantic.icon.disabled,
      },
    },
    neutral: {
      enabled: {
        bg: semantic.bg.surfaceSubtle,
        text: semantic.text.primary,
        icon: semantic.icon.primary,
      },
      hover: {
        bg: semantic.bg.surfaceDefault,
        text: semantic.text.primary,
        icon: semantic.icon.primary,
      },
      focussed: {
        bg: semantic.bg.surfaceDefault,
        text: semantic.text.primary,
        icon: semantic.icon.primary,
      },
      pressed: {
        bg: semantic.bg.primary,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      disabled: {
        bg: semantic.bg.disabled,
        text: semantic.text.disabled,
        icon: semantic.icon.disabled,
      },
    },
    destructive: {
      enabled: {
        bg: semantic.bg.dangerStrong,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      hover: {
        bg: semantic.bg.surfaceSubtle,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      focussed: {
        bg: semantic.bg.surfaceInverseSubtle,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      pressed: {
        bg: semantic.bg.surfaceInverseStrong,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      disabled: {
        bg: semantic.bg.disabled,
        text: semantic.text.disabled,
        icon: semantic.icon.disabled,
      },
    },
    transparent: {
      enabled: {
        bg: semantic.bg.surfaceSubtle,
        text: semantic.text.primary,
        icon: semantic.icon.primary,
      },
      hover: {
        bg: semantic.bg.surfaceSubtle,
        text: semantic.text.primary,
        icon: semantic.icon.primary,
      },
      focussed: {
        bg: semantic.bg.surfaceInverseSubtle,
        text: semantic.text.primary,
        icon: semantic.icon.primary,
      },
      pressed: {
        bg: semantic.bg.secondary,
        text: semantic.text.primary,
        icon: semantic.icon.primary,
      },
      disabled: {
        bg: semantic.bg.disabled,
        text: semantic.text.disabled,
        icon: semantic.icon.disabled,
      },
    },
    blur: {
      enabled: {
        bg: semantic.bg.surfaceInverseSubtle,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      hover: {
        bg: semantic.bg.surfaceInverseStrong,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      focussed: {
        bg: semantic.bg.surfaceInverseSubtle,
        text: semantic.text.disabled,
        icon: semantic.icon.disabled,
      },
      pressed: {
        bg: semantic.bg.surfaceInverseStrong,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      disabled: {
        bg: semantic.bg.surfaceInverseSubtle,
        text: semantic.text.disabled,
        icon: semantic.icon.disabled,
      },
    },
  },
} as const;

const brandHeadingFamily = primitives.typography.fontFamily.brandPrimary;
const brandBodyFamily = primitives.typography.fontFamily.brandSecondary;

export const textStyle = {
  display: {
    xl: {
      fontFamily: brandHeadingFamily,
      fontSize: primitives.typography['scale-text']['scale-text-1100'],
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    lg: {
      fontFamily: brandHeadingFamily,
      fontSize: primitives.typography['scale-text']['scale-text-1000'],
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    md: {
      fontFamily: brandHeadingFamily,
      fontSize: primitives.typography['scale-text']['scale-text-800'],
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    sm: {
      fontFamily: brandHeadingFamily,
      fontSize: primitives.typography['scale-text']['scale-text-600'],
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    'size-lg': {
      fontFamily: brandHeadingFamily,
      fontSize: `var(--voyager-display-lg-size, ${primitives.typography['scale-text']['scale-text-900']})`,
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    'size-md': {
      fontFamily: brandHeadingFamily,
      fontSize: `var(--voyager-display-md-size, ${primitives.typography['scale-text']['scale-text-700']})`,
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
  },
  headline: {
    xl: {
      fontFamily: brandHeadingFamily,
      fontSize: primitives.typography['scale-text']['scale-text-600'],
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    lg: {
      fontFamily: brandHeadingFamily,
      fontSize: primitives.typography['scale-text']['scale-text-500'],
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    md: {
      fontFamily: brandHeadingFamily,
      fontSize: '1.125rem',
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    sm: {
      fontFamily: brandHeadingFamily,
      fontSize: primitives.typography['scale-text']['scale-text-400'],
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    'size-lg': {
      fontFamily: brandHeadingFamily,
      fontSize: `var(--voyager-headline-lg-size, ${primitives.typography['scale-text']['scale-text-700']})`,
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    'size-md': {
      fontFamily: brandHeadingFamily,
      fontSize: `var(--voyager-headline-md-size, ${primitives.typography['scale-text']['scale-text-600']})`,
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    'size-sm': {
      fontFamily: brandHeadingFamily,
      fontSize: `var(--voyager-headline-sm-size, ${primitives.typography['scale-text']['scale-text-500']})`,
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
  },
  body: {
    lg: {
      regular: {
        fontFamily: brandBodyFamily,
        fontSize: primitives.typography['scale-text']['scale-text-400'],
        fontWeight: fonts.weight.normal,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
      strong: {
        fontFamily: brandBodyFamily,
        fontSize: primitives.typography['scale-text']['scale-text-400'],
        fontWeight: fonts.weight.medium,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
      link: {
        fontFamily: brandBodyFamily,
        fontSize: primitives.typography['scale-text']['scale-text-400'],
        fontWeight: fonts.weight.medium,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
    },
    md: {
      regular: {
        fontFamily: brandBodyFamily,
        fontSize: primitives.typography['scale-text']['scale-text-300'],
        fontWeight: fonts.weight.normal,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
      strong: {
        fontFamily: brandBodyFamily,
        fontSize: primitives.typography['scale-text']['scale-text-300'],
        fontWeight: fonts.weight.medium,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
      link: {
        fontFamily: brandBodyFamily,
        fontSize: primitives.typography['scale-text']['scale-text-300'],
        fontWeight: fonts.weight.medium,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
    },
    sm: {
      regular: {
        fontFamily: brandBodyFamily,
        fontSize: primitives.typography['scale-text']['scale-text-200'],
        fontWeight: fonts.weight.normal,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
      strong: {
        fontFamily: brandBodyFamily,
        fontSize: primitives.typography['scale-text']['scale-text-200'],
        fontWeight: fonts.weight.medium,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
      link: {
        fontFamily: brandBodyFamily,
        fontSize: primitives.typography['scale-text']['scale-text-200'],
        fontWeight: fonts.weight.medium,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
    },
    xs: {
      regular: {
        fontFamily: brandBodyFamily,
        fontSize: primitives.typography['scale-text']['scale-text-100'],
        fontWeight: fonts.weight.normal,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
      strong: {
        fontFamily: brandBodyFamily,
        fontSize: primitives.typography['scale-text']['scale-text-100'],
        fontWeight: fonts.weight.medium,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
      link: {
        fontFamily: brandBodyFamily,
        fontSize: primitives.typography['scale-text']['scale-text-100'],
        fontWeight: fonts.weight.medium,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
    },
  },
  title: {
    lg: {
      fontFamily: brandBodyFamily,
      fontSize: `var(--voyager-title-lg-size, ${primitives.typography['scale-text']['scale-text-600']})`,
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.normal,
    },
    md: {
      fontFamily: brandBodyFamily,
      fontSize: `var(--voyager-title-md-size, ${primitives.typography['scale-text']['scale-text-500']})`,
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.normal,
    },
    sm: {
      fontFamily: brandBodyFamily,
      fontSize: `var(--voyager-title-sm-size, ${primitives.typography['scale-text']['scale-text-400']})`,
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.normal,
    },
  },
} as const;

export const colorVarNames = {
  primary: '--voyager-color-primary',
} as const;

export const fontVarNames = {
  heading: '--voyager-heading-font',
  body: '--voyager-body-font',
} as const;

export const cardAspectRatio = {
  category: { desktop: '3 / 2', mobile: '6 / 7' },
} as const;
